<template>
  <div class="report-table q-mt-md">
    <q-banner v-if="rows.length === 0" dense rounded class="bg-grey-2">
      Данных для отображения нет. Измените период или сформируйте отчет повторно.
    </q-banner>

    <div v-else class="report-table__scroll">
      <q-markup-table v-accessible-scroll-region="'Показатели отчета'" flat bordered dense separator="cell">
        <thead>
          <tr>
            <th class="report-table__sticky text-left">Строка</th>
            <th class="text-left report-table__code">Код</th>
            <th
              v-for="column in effectiveColumns"
              :key="column.name"
              class="text-right report-table__value"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in displayRows" :key="item.id" :class="rowClass(item)">
            <td v-if="item.type === 'section'" :colspan="effectiveColumns.length + 2" class="report-table__section">
              {{ item.name }}
            </td>
            <template v-else>
              <td class="report-table__sticky text-left">
                <div class="row no-wrap items-center" :style="{ paddingLeft: `${item.level * 16}px` }">
                  <q-btn
                    v-if="item.children && item.children.length"
                    flat
                    dense
                    round
                    size="sm"
                    :icon="isExpanded(item) ? 'expand_more' : 'chevron_right'"
                    @click.stop="toggleRow(item)"
                  />
                  <span v-else class="report-table__indent" />
                  <span>{{ item.name }}</span>
                </div>
              </td>
              <td class="text-left report-table__code">{{ item.code || '—' }}</td>
              <td
                v-for="column in effectiveColumns"
                :key="column.name"
                class="text-right report-table__value"
                :class="{ 'text-negative': isNegative(item.values && item.values[column.name]) }"
              >
                {{ formatCell(item.values && item.values[column.name]) }}
              </td>
            </template>
          </tr>
        </tbody>
      </q-markup-table>
    </div>

    <div v-if="measureLabel" class="text-caption text-grey-7 q-mt-sm">
      Единица измерения: {{ measureLabel }}
    </div>
  </div>
</template>

<script>
import { formatMoney } from '../utils/reportFormatters'

export default {
  name: 'ReportTable',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    valueColumns: {
      type: Array,
      default: () => []
    },
    measureLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      expanded: {}
    }
  },
  computed: {
    effectiveColumns() {
      if (this.valueColumns.length > 0) {
        return this.valueColumns
      }
      const names = []
      this.rows.forEach((row) => {
        Object.keys(row.values || {}).forEach((key) => {
          if (!names.includes(key)) {
            names.push(key)
          }
        })
      })
      return names.map((name) => ({ name, label: name }))
    },
    displayRows() {
      const out = []
      let section = null
      this.rows.forEach((row, index) => {
        if (row.section && row.section !== section) {
          section = row.section
          out.push({
            type: 'section',
            id: `section-${section}-${index}`,
            name: section
          })
        }
        this.pushRow(out, row, 0)
      })
      return out
    }
  },
  methods: {
    pushRow(out, row, level) {
      const item = {
        ...row,
        id: row.id || row.key || `${row.name}-${level}`,
        level
      }
      out.push(item)
      if (!item.children || !item.children.length || !this.isExpanded(item)) {
        return
      }
      item.children.forEach((child) => this.pushRow(out, child, level + 1))
    },
    isExpanded(row) {
      return this.expanded[row.id || row.key] !== false
    },
    toggleRow(row) {
      const id = row.id || row.key
      this.expanded = {
        ...this.expanded,
        [id]: !this.isExpanded(row)
      }
    },
    rowClass(row) {
      if (row.type === 'section') {
        return 'bg-grey-2 text-weight-medium'
      }
      if (row.isTotal) {
        return 'report-table__total'
      }
      return ''
    },
    isNegative(value) {
      if (value === null || value === undefined || value === '') {
        return false
      }
      return Number(String(value).replace(/\s/g, '').replace(',', '.')) < 0
    },
    formatCell(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style scoped>
.report-table__scroll {
  max-width: 100%;
  overflow-x: auto;
}

.report-table__sticky {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: 280px;
  max-width: 420px;
  background: var(--sid-color-surface-0);
}

.report-table__code {
  min-width: 90px;
}

.report-table__value {
  min-width: 140px;
}

.report-table__section {
  font-weight: 600;
  background: var(--sid-color-surface-subtle);
}

.report-table__total td {
  font-weight: 600;
  background: var(--sid-color-surface-canvas);
}

.report-table__total .report-table__sticky {
  background: var(--sid-color-surface-canvas);
}

.report-table__indent {
  display: inline-block;
  width: 28px;
  flex: 0 0 28px;
}
</style>
